import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { RxFor } from '@rx-angular/template/for';
import {
  AccurateDateInterval,
  ShiftColorOptions,
  StaffLayoutStyle,
  UnassignedShift,
} from '@wilson/interfaces';
import { isDebugEnabled } from '@wilson/non-domain-specific/logger/logger';
import { determineUnassignedShiftsOverlaps } from '@wilson/non-domain-specific/overlap-helpers';
import { DatesDemarcatorLinesComponent } from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  DragAndDropModeService,
  PreferredSettingsStateFacadeService,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineDataState,
  ShiftTimelineSettingsState,
  ShiftTimelineState,
  ShiftTimelineUnassignedRegionState,
  AssignedRegionVisibilityStateService,
  UnassignedRegionVisibleShiftsStateService,
} from '@wilson/shift-timeline/state';
import {
  wilsonCancelIdleCallback,
  wilsonRequestIdleCallback,
} from '@wilson/utils';
import { BehaviorSubject, debounceTime, Observable, Subscription } from 'rxjs';
import { UnassignedShiftWithoutActivitiesComponent } from '../unassigned-shift-without-activities/unassigned-shift-without-activities.component';

@Component({
  selector: 'wilson-unassigned-shift-without-activities-tab',
  templateUrl: './unassigned-shift-without-activities-tab.component.html',
  styleUrl: './unassigned-shift-without-activities-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ScrollingModule,
    UnassignedShiftWithoutActivitiesComponent,
    RxFor,
    DatesDemarcatorLinesComponent,
  ],
})
export class UnassignedShiftWithoutActivitiesTabComponent
  implements OnInit, OnDestroy
{
  readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly unassignedRegionVisibleShiftsStateService = inject(
    UnassignedRegionVisibleShiftsStateService,
  );
  private readonly preferredSettingsStateFacadeService = inject(
    PreferredSettingsStateFacadeService,
  );
  readonly assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );

  private store = inject(Store);
  private subscription = new Subscription();
  private shiftsSubject = new BehaviorSubject<UnassignedShift[]>([]);
  private readonly visibleRowsSignal = signal<{
    start: number;
    end: number;
  }>({
    start: 0,
    end: 0,
  });

  @ViewChild('unassignedItemsContainer')
  unassignedItemsContainer!: ElementRef;

  @Input()
  set shifts(value: UnassignedShift[] | null) {
    this.shiftsSubject.next(value || []);
  }

  @Input()
  rowHeight!: number;

  @Input()
  set visibleRows(value: { start: number; end: number } | null) {
    if (value) {
      this.visibleRowsSignal.set(value);
    }
  }

  @Input()
  pixelWidthPerDay!: number;

  protected maxHeight$ = this.store.select(
    ShiftTimelineDataState.maxUnassignedRowHeightPx,
  );

  protected isUnassignedRegionExpanded$ = this.store.select(
    ShiftTimelineUnassignedRegionState.isUnassignedRegionExpanded,
  );

  protected defaultUnassignedSectionHeight$ = this.store.select(
    ShiftTimelineSettingsState.defaultUnassignedSectionHeight,
  );

  protected areActivitiesVisible$ = this.store.select(
    ShiftTimelineSettingsState.areActivitiesVisible,
  );

  protected visibleDaysRange$ = this.store.select(
    ShiftTimelineDataState.visibleDateRange,
  );

  protected readonly maxRowIndexSignal = signal(0);
  protected readonly shiftRowIndexMapSignal = signal<{
    [shiftId: string]: number;
  }>({});
  protected readonly rowsSignal = signal<
    Record<
      'shifts',
      Array<
        AccurateDateInterval & {
          shift: UnassignedShift;
        }
      >
    >[]
  >([]);
  protected timeframe$ = this.store.select(ShiftTimelineDataState.timeframe);

  protected visibleShiftsSignal!: Signal<UnassignedShift[]>;
  protected shiftColorOption$!: Observable<ShiftColorOptions>;
  protected staffLayoutStyle$!: Observable<StaffLayoutStyle>;
  private idleCallbackId: ReturnType<typeof wilsonRequestIdleCallback> | null =
    null;

  trackByShiftId(_: number, data: UnassignedShift): string {
    return data.id;
  }

  trackByDateString: TrackByFunction<Date> = (_: number, day: Date) => {
    return day.toDateString();
  };

  protected frameLength$ = this.store.select(ShiftTimelineState.frameLength);

  ngOnInit(): void {
    this.shiftColorOption$ =
      this.preferredSettingsStateFacadeService.shiftColorOptions$;

    this.staffLayoutStyle$ =
      this.preferredSettingsStateFacadeService.staffLayoutStyle$;

    this.visibleShiftsSignal = computed(() => {
      const overloadRange = 2;
      const visibleRowsRange = this.visibleRowsSignal();
      const rows = this.rowsSignal();
      const overloadedStart = Math.max(
        0,
        visibleRowsRange.start - overloadRange,
      );
      const visibleRows = rows.slice(
        overloadedStart,
        visibleRowsRange.end + overloadRange,
      );
      const visibleShiftIds = new Set<string>();
      const visibleShifts = visibleRows.reduce((acc, row, index) => {
        row.shifts.forEach((shiftData) => {
          acc.push(shiftData.shift);
          if (
            index >= visibleRowsRange.start &&
            index <= visibleRowsRange.end
          ) {
            visibleShiftIds.add(shiftData.shift.id);
          }
        });
        return acc;
      }, [] as UnassignedShift[]);

      if (this.idleCallbackId) {
        wilsonCancelIdleCallback(this.idleCallbackId);
      }
      this.idleCallbackId = wilsonRequestIdleCallback(() => {
        this.unassignedRegionVisibleShiftsStateService.updateVisibilityForShifts(
          visibleShiftIds,
        );
      }, 100);

      return visibleShifts;
    });

    this.subscription.add(
      this.shiftsSubject.pipe(debounceTime(100)).subscribe((shifts) => {
        const overlapData = determineUnassignedShiftsOverlaps({
          shifts,
          isDebugEnabled: isDebugEnabled(),
        });

        this.maxRowIndexSignal.set(overlapData.maxRowIndex);
        this.shiftRowIndexMapSignal.set(overlapData.shiftRowIndexMap);
        this.rowsSignal.set(overlapData.rows);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.shiftsSubject = null as any;
    this.maxHeight$ = null as any;
    this.isUnassignedRegionExpanded$ = null as any;
    this.defaultUnassignedSectionHeight$ = null as any;
  }
}
